import React, { useState, useEffect } from 'react';
import adminAPI from '../../lib/api';
import { toast } from 'sonner';
import { Save, Bot } from 'lucide-react';

export default function CoachConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getCoachConfig();
      setConfig(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { key, ...rest } = config;
      await adminAPI.updateCoachConfig(rest);
      toast.success('Konfigurasjon lagret');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateFeature = (feat, val) => {
    setConfig(prev => ({ ...prev, features: { ...prev.features, [feat]: val } }));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!config) return <div className="text-center py-20 text-white/30">Kunne ikke laste konfigurasjon</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">Coach-konfigurasjon</h1>
          <p className="text-xs sm:text-sm text-white/40 mt-1">Juster AI-coachens oppførsel og innstillinger</p>
        </div>
        <button
          data-testid="admin-coach-save-button"
          onClick={handleSave}
          disabled={saving}
          className="h-11 px-6 rounded-[14px] bg-[#CCFF00] text-black font-semibold text-sm flex items-center gap-2 hover:bg-[#D7FF3A] active:scale-[0.99] transition-[background-color,transform] duration-180 shadow-[0_8px_24px_rgba(204,255,0,0.12)] disabled:opacity-50"
        >
          {saving ? <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
          Lagre
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main config */}
        <div className="lg:col-span-2 space-y-6">
          {/* System prompt */}
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">SYSTEM PROMPT</p>
            <textarea
              data-testid="admin-coach-prompt-textarea"
              value={config.system_prompt || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, system_prompt: e.target.value }))}
              rows={10}
              className="w-full rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white/90 p-4 placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 resize-y font-mono transition-[box-shadow] duration-180"
            />
          </div>

          {/* Model and personality */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
              <label className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-3 block">MODELL</label>
              <input
                data-testid="admin-coach-model-select"
                type="text"
                value={config.model || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, model: e.target.value }))}
                className="w-full h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 transition-[box-shadow] duration-180"
              />
              <p className="text-[10px] text-white/20 mt-2">Provider: {config.provider || 'openai'}</p>
            </div>
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
              <label className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-3 block">PERSONLIGHET</label>
              <div className="flex gap-2">
                {['supportive', 'balanced', 'hard'].map(p => (
                  <button
                    key={p}
                    onClick={() => setConfig(prev => ({ ...prev, personality: p }))}
                    className={`flex-1 h-11 rounded-[14px] text-sm font-medium capitalize transition-[background-color,border-color,color] duration-180 border ${
                      config.personality === p
                        ? 'bg-[#CCFF00]/10 border-[#CCFF00]/30 text-[#CCFF00]'
                        : 'bg-transparent border-white/10 text-white/40 hover:text-white/60 hover:border-white/15'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Chat limit */}
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
            <label className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-3 block">DAGLIG CHAT-GRENSE (GRATIS)</label>
            <input
              type="number"
              value={config.daily_chat_limit_free || 10}
              onChange={(e) => setConfig(prev => ({ ...prev, daily_chat_limit_free: parseInt(e.target.value) || 0 }))}
              className="w-32 h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 transition-[box-shadow] duration-180"
            />
            <p className="text-[10px] text-white/20 mt-2">Antall meldinger per dag for gratisbrukere</p>
          </div>
        </div>

        {/* Feature toggles sidebar */}
        <div className="space-y-6">
          <div data-testid="admin-coach-feature-toggles" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">FUNKSJONER</p>
            <div className="space-y-4">
              {config.features && Object.entries(config.features).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm text-white/70 capitalize">{key}</span>
                  <button
                    onClick={() => updateFeature(key, !val)}
                    className={`w-11 h-6 rounded-full relative transition-[background-color] duration-180 ${
                      val ? 'bg-[#CCFF00]' : 'bg-white/10'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full transition-[left,background-color] duration-180 ${
                      val ? 'left-6 bg-black' : 'left-1 bg-white/50'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">FORHÅNDSVISNING</p>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#CCFF00]/10 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-[#CCFF00]" />
              </div>
              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
                <p className="text-sm text-white/70 leading-relaxed">
                  {config.personality === 'hard' && '"Du løp for hardt i går. Rolig i dag. Stol på prosessen."'}
                  {config.personality === 'balanced' && '"God økt i går. I dag foreslår jeg en rolig tur på 6km."'}
                  {config.personality === 'supportive' && '"Bra jobbet i går! Ta det rolig i dag, kroppen trenger det."'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
